import { Fragment, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MARKETPLACE_CURRENCY_DENOMINATION } from "../../../../utils/marketplaceConstants";
import FormGroupTile from "../../basic/FormGroupTile";
import { Flex } from "../../../../../styled-system/jsx";
import { toCurrency } from "../../../../utils/marketplaceConstants";
import { AdPlanContext } from "./AdPlanContext";

import { AcosGoalsField } from "./fields";
import { getCombinedTemplates, TACTIC_LABEL_SELECTORS } from "./componentTemplates";
import { trellisPalette } from "../../../custom/analytics/palettes";
import { Link } from "@tanstack/react-router";
import { getAdTypeName } from "../../../../utils/getAdTypeName";
import channel from "../../../../redux/reducers/channel";
import CampaignNameField from "./fields/CampaignNameField";
import checkModule from "../../../../utils/checkModule";

const AdPlanChildren = ({
  state,
  children,
  onSelect,
  adId,
  marketPlace,
  errors,
  categoryId,
  sectionEdit,
  user,
  onChange,
  fieldsToValidate,
  review,
  onShowChild,
  modules,
  roas,
  isDisabled,
}) => {
  const { t } = useTranslation(["advertising"]);
  const { savedChildPlans, setSavedChildPlans } = useContext(AdPlanContext);

  const renderTacticLabel = (name) => {
    const parts = name.split(" - ");
    const last = parts[parts.length - 1];
    const selector = TACTIC_LABEL_SELECTORS[last];
    return selector ? t(selector) : (last ?? name);
  };

  // Populate children on initial render
  useEffect(() => {
    if (!state.children?.length && !state.id) {
      const componentMediaPlans = getCombinedTemplates(categoryId, state.product, state.name, roas);
      onChange({
        children: [...componentMediaPlans],
      });

      setSavedChildPlans([...componentMediaPlans]);
    }

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  // Ensure new children are saved
  if (children?.length > state.children?.length) {
    onChange({ children: children });
  }

  const totalAllocation =
    children
      ?.filter((c) => c.advStatus !== 4 && c.advStatus !== 3 && c.advStatus !== 10)
      ?.reduce((runningTotal, current) => runningTotal + parseFloat(current.allocation), 0) ?? 0;

  return (
    <>
      <Flex wrap="wrap">
        <>
          {!adId && !review && (
            <div style={{ maxWidth: "90%" }}>
              <h2 style={{ fontWeight: 700, marginTop: 0, fontSize: "24px" }}>
                {review ?
                  t(($) => $.advertising["Review your Tactics"])
                : t(($) => $.advertising["Build your Tactics"])}
              </h2>

              {!review && (
                <p style={{ fontSize: "16px" }}>
                  {t(
                    ($) =>
                      $.advertising[
                        "Choose a template and set targeting parameters to build tactics that work for you."
                      ],
                  )}
                </p>
              )}
            </div>
          )}
          <Flex wrap="wrap">
            <div style={{ padding: "1rem 2rem" }}>
              <FormGroupTile
                type="formcontrol"
                label={t(($) => $.advertising["Daily Budget"])}
                formControlType={"number"}
                toolTip={t(
                  ($) => $.advertising["The daily maximum amount you want to spend on advertising for this plan."],
                )}
                stateName={"dailyBudget"}
                prefix={"$"}
                handleChildFormElement={(key, value) => onChange({ [key]: value })}
                placeholder={t(($) => $.advertising["Enter Daily Budget ({currency})"], {
                  currency: MARKETPLACE_CURRENCY_DENOMINATION[marketPlace],
                })}
                defaultValue={state.dailyBudget}
                disabled={isDisabled}
              />
              {errors.dailyBudget && fieldsToValidate?.includes("dailyBudget") && (
                <small className="text-danger" style={{ paddingLeft: "2rem" }}>
                  {errors.dailyBudget}
                </small>
              )}
            </div>
          </Flex>
        </>
      </Flex>
      <div style={{ maxWidth: "90%" }}>
        <span
          style={{
            fontSize: "20px",
            padding: "1rem 0",
            display: "block",
            fontWeight: 500,
          }}
        >
          {t(($) => $.advertising["Component Settings"])}
        </span>
        <p style={{ fontSize: "16px", marginBottom: "20px" }}>
          {t(
            ($) =>
              $.advertising[
                "Adjust components to customize your targeting options for some or all of your selected products."
              ],
          )}
        </p>
      </div>
      <div style={{ paddingRight: "2rem" }}>
        {errors.campaignNames && fieldsToValidate?.includes("campaignNames") && (
          <small className="text-danger d-block pb-4">{errors.campaignNames}</small>
        )}
        {errors.duplicateSegmentedCampaignNames && fieldsToValidate?.includes("duplicateSegmentedCampaignNames") && (
          <small className="text-danger d-block pb-4">{errors.duplicateSegmentedCampaignNames}</small>
        )}

        <div style={{ marginBottom: "2em" }}>
          {/* Component Summary */}
          {children?.map((child, i) => {
            if (child.advStatus === 4) {
              return null;
            }

            const [campaignType, campaignName] = Object.entries(child?.campaignNames ?? {})?.[0] ?? [];

            const handleCampaignNameChange = (type, value) => {
              const updatedCampaignName = {
                [type]: value,
              };

              const updatedChildren = children?.map((child, index) =>
                index === i ? { ...child, campaignNames: updatedCampaignName } : child,
              );

              setSavedChildPlans(updatedChildren);
              onChange({ children: updatedChildren });
            };

            return (
              <Fragment key={child.id}>
                {i === 0 && (
                  <div
                    key={i}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "170px 1fr 410px 0",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "5px 16px 5px 16px",
                      border: "1px solid #e3e3e3",
                      borderTop: i === 0 ? "1px solid #e3e3e3" : "none",
                    }}
                  >
                    <div>
                      <h6
                        style={{
                          paddingLeft: "20px",
                          textTransform: "none",
                          display: "flex",
                          alignItems: "center",
                          color: "#73706E",
                        }}
                      >
                        {t(($) => $.advertising["Tactic"])}
                      </h6>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        color: "#73706E",
                      }}
                    >
                      <h6
                        style={{
                          padding: "0 1rem",
                          textTransform: "none",
                          display: "flex",
                          alignItems: "center",
                          color: "#73706E",
                        }}
                      >
                        {t(($) => $.advertising["Ad Type"])}
                      </h6>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        color: "#73706E",
                      }}
                    >
                      <h6
                        style={{
                          padding: "0 1rem",
                          textTransform: "none",
                          textAlign: "center",
                          display: "flex",
                          alignItems: "center",
                          color: "#73706E",
                        }}
                      >
                        {roas ?
                          t(($) => $.advertising["Optimal ROAS Range"])
                        : t(($) => $.advertising["Optimal ACOS Range"])}
                      </h6>
                      <h6
                        style={{
                          padding: "0 1rem",
                          textTransform: "none",
                          textAlign: "center",
                          color: "#73706E",
                        }}
                      ></h6>
                      <h6
                        style={{
                          padding: "0 1rem",
                          textTransform: "none",
                          textAlign: "center",
                          width: "155px",
                          color: "#73706E",
                        }}
                      >
                        {t(($) => $.advertising["Budget Allocation (%)"])}
                      </h6>
                    </div>
                  </div>
                )}

                <div
                  key={i}
                  style={{
                    display: "grid",
                    gridTemplateColumns: review && !adId ? "160px 1fr 440px 0" : "170px 1fr 410px 0",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: review ? "24px 16px" : "15px 16px ",
                    border: "1px solid #e3e3e3",
                    borderTop: i === 0 ? "1px solid #e3e3e3" : "none",
                  }}
                >
                  <div
                    style={{
                      marginLeft: "20px",
                    }}
                  >
                    {state.id ?
                      <Link
                        to="/user/advertising/dashboard/ads/$id"
                        params={{ id: String(child.id) }}
                        style={{
                          color: trellisPalette[0],
                          textDecoration: "none",
                          fontWeight: 600,
                        }}
                        className="fs-standard"
                      >
                        {renderTacticLabel(child.name)}
                      </Link>
                    : <span
                        onClick={() => {
                          if (!adId || !child.id) {
                            return;
                          }

                          !sectionEdit && onSelect(i, child);
                          onShowChild && onShowChild(i, child);
                        }}
                        style={{
                          color: adId ? trellisPalette[0] : "#333",
                          cursor: adId ? "pointer" : "inherit",
                          fontWeight: 600,
                        }}
                      >
                        <span
                          style={{
                            visibility: sectionEdit ? "hidden" : "inherit",
                          }}
                          className="fs-standard"
                        >
                          {renderTacticLabel(child.name)}
                        </span>
                      </span>
                    }
                  </div>
                  {/* Ad type field */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                    className="fs-standard"
                  >
                    <div>
                      {(() => {
                        const sel = getAdTypeName(child.adType, channel);
                        return sel ? t(sel) : "";
                      })()}
                      {(state.dailyBudget / 100.0) * child.allocation < 1 &&
                        child.advStatus !== 10 &&
                        child.advStatus !== 3 && (
                          <div
                            style={{
                              marginRight: "2rem",
                              textAlign: "left",
                            }}
                          >
                            <i className="fa fa-warning" />{" "}
                            {t(($) => $.advertising["Budget allocation is less than {amount}."], {
                              amount: toCurrency(1, marketPlace.marketPlace),
                            })}
                          </div>
                        )}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {review && !adId && (
                      <>
                        {" "}
                        <div style={{ width: "200px", textAlign: "center" }}>
                          {roas ?
                            <>
                              <label
                                className="m-1 py-0"
                                style={{
                                  color: "#73706E",
                                  fontWeight: "700",
                                  textTransform: "none",
                                }}
                              >
                                {t(($) => $.advertising["Low"])}
                              </label>
                              <div>{child.maxACOS}</div>
                            </>
                          : `${child.targetACOS}%`}
                        </div>
                        <div style={{ width: "200px", textAlign: "center" }}>
                          {roas ?
                            <>
                              <label
                                className="m-1 py-0"
                                style={{
                                  color: "#73706E",
                                  fontWeight: "700",
                                  textTransform: "none",
                                }}
                              >
                                {t(($) => $.advertising["High"])}
                              </label>
                              <div>{child.targetACOS}</div>
                            </>
                          : `${child.maxACOS}%`}
                        </div>
                      </>
                    )}
                    <div
                      style={{
                        marginLeft: review && !adId ? "inherit" : "auto",
                        marginRight: review && !adId ? "-3.5rem" : "inherit",
                        width: review && !adId ? "200px" : "inherit",
                      }}
                    >
                      {child.advStatus === 10 ?
                        <> {t(($) => $.advertising["Ad Failed"])}</>
                      : child.advStatus === 3 ?
                        <>{t(($) => $.advertising["Ad Paused"])}</>
                      : review && !adId ?
                        <div
                          style={{
                            textAlign: review && !adId ? "center" : "inherit",
                          }}
                          className="fs-standard"
                        >
                          {child.allocation}%
                          <span>
                            {state.dailyBudget ?
                              ` (${toCurrency((state.dailyBudget / 100.0) * child.allocation, marketPlace.marketPlace)})`
                            : ""}
                          </span>
                        </div>
                      : <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <div>
                            <AcosGoalsField
                              state={child}
                              onChange={onChange}
                              user={user}
                              marketPlace={marketPlace}
                              errors={errors}
                              isChild={true}
                              adId={adId}
                              roas={roas}
                              channel={"amazon"}
                              fieldsToValidate={fieldsToValidate}
                              review={review}
                              hideLabels={true}
                              childIndex={i}
                              savedChildPlans={savedChildPlans}
                              setSavedChildPlans={setSavedChildPlans}
                              allowChildEdit={true}
                              modules={modules}
                              isDisabled={isDisabled}
                            />
                          </div>
                          <div
                            style={{
                              dispay: "flex",
                              justifyContent: "center",
                              marginLeft: "2rem",
                            }}
                          >
                            {toCurrency(
                              parseFloat((state.dailyBudget / 100) * child.allocation),
                              marketPlace.marketPlace,
                            )}
                          </div>
                        </div>
                      }
                    </div>
                  </div>

                  {/* Below logic is to only show campaign name inputs if module enabled during creation
                    in components table, then only show if a names been entered in the review step */}
                  {checkModule(modules, "CAMPAIGN_NAMES") && (!review || (review && !state.id && campaignName)) && (
                    <div style={{ gridColumn: "1 / -1" }}>
                      <CampaignNameField
                        campaignType={campaignType}
                        campaignName={campaignName}
                        state={state}
                        handleCampaignNameChange={handleCampaignNameChange}
                        key={`${campaignType}-${i}`}
                        marketPlace={marketPlace.marketPlace}
                        labelClassName={"inherit-text-styles"}
                        overrideLabelText={
                          review ?
                            t(($) => $.advertising["Campaign Name"])
                          : t(($) => $.advertising["Campaign Name (optional)"])
                        }
                        overridePlaceholderText={t(($) => $.advertising["Campaign Name"])}
                        index={i}
                        review={review}
                        customLabelStyles={review ? { marginTop: "1rem" } : {}}
                        formControlClasses="border"
                      />
                    </div>
                  )}
                </div>
              </Fragment>
            );
          })}
          {/* Total Row */}
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              padding: "12px 16px",
              border: "1px solid #e3e3e3",
              borderTop: "none",
            }}
          >
            <div style={{ paddingLeft: "2rem" }} className="fs-standard">
              <strong>{t(($) => $.advertising["Total Allocation:"])}</strong>
            </div>
            <div style={{ padding: "0 2rem" }} className="fs-standard">
              {totalAllocation < 100 ?
                <>
                  <i className="fa fa-warning" />{" "}
                  {t(($) => $.advertising["Only {percent}% of this media plan's budget is currently allocated."], {
                    percent: totalAllocation,
                  })}
                </>
              : totalAllocation > 100 ?
                <>
                  <i className="fa fa-warning" /> {t(($) => $.advertising["Budget allocation exceeds 100%."])}
                </>
              : null}
            </div>{" "}
            <div
              style={{
                marginLeft: "auto",
              }}
              className="fs-standard"
            >
              {totalAllocation}%{" "}
              <strong>
                ({toCurrency(parseFloat((state.dailyBudget / 100) * totalAllocation), marketPlace.marketPlace)})
              </strong>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            {errors.children && (
              <h6 className="text-danger" style={{ margin: "1rem" }}>
                {errors.children}
              </h6>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AdPlanChildren;
